import React from 'react';
import { PiLockKeyLight } from 'react-icons/pi';
import { ProgressStepsModal } from 'src/modules/shared/components/organisms/progress-steps-modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export type WhatsAppSignupProgressPhase = 'meta' | 'finalizing';

interface WhatsAppSignupProgressModalProps {
  /** Whether the onboarding dialog is visible. */
  isOpen: boolean;
  /** Determines whether local cancellation is still safe. */
  phase: WhatsAppSignupProgressPhase;
  /** Retires the active Meta attempt before dismissing the dialog. */
  onCancel: () => void;
}

const ignoreDismissal = () => undefined;

/** Keeps the parent tab stable while Meta and Comos complete onboarding. */
export const WhatsAppSignupProgressModal: React.FC<
  WhatsAppSignupProgressModalProps
> = ({ isOpen, phase, onCancel }) => {
  const { t } = useI18n();
  const isFinalizing = phase === 'finalizing';
  const title = t('settings.integrations.whatsapp.progress.title');

  return (
    <ProgressStepsModal
      isOpen={isOpen}
      onClose={isFinalizing ? ignoreDismissal : onCancel}
      dismissible={!isFinalizing}
      title={title}
      subtitle={t('settings.integrations.whatsapp.progress.subtitle')}
      icon={<PiLockKeyLight className="h-5 w-5" aria-hidden="true" />}
      activeVariant="whatsapp"
      noticeVariant="warning"
      steps={[
        {
          key: 'meta',
          label: t('settings.integrations.whatsapp.progress.meta.label'),
          description: t(
            isFinalizing
              ? 'settings.integrations.whatsapp.progress.meta.complete'
              : 'settings.integrations.whatsapp.progress.meta.active'
          ),
          state: isFinalizing ? 'complete' : 'active',
        },
        {
          key: 'finalizing',
          label: t('settings.integrations.whatsapp.progress.finalizing.label'),
          description: t(
            isFinalizing
              ? 'settings.integrations.whatsapp.progress.finalizing.active'
              : 'settings.integrations.whatsapp.progress.finalizing.pending'
          ),
          state: isFinalizing ? 'active' : 'pending',
        },
      ]}
      notice={t(
        isFinalizing
          ? 'settings.integrations.whatsapp.progress.finalizing.notice'
          : 'settings.integrations.whatsapp.progress.meta.notice'
      )}
    />
  );
};
